import React from "react";
import { Box, Typography } from "@mui/material";
import {
  Inventory2Outlined,
  PaymentsOutlined,
  StickyNote2Outlined,
  DescriptionOutlined,
  LocalShippingOutlined,
} from "@mui/icons-material";

import CompactCard from "../../ShipmentRequest/ShipmentComponents/CompactCard";
import ShipmentNotesCompact from "../../ShipmentRequest/ShipmentComponents/ShipmentNotesCompact";
import ShipmentDocumentsCompact from "../../ShipmentRequest/ShipmentComponents/ShipmentDocumentsCompact";
import { useDeliveryDetailContext } from "contexts/DeliveryDetailContext";
import { localizeDateTimeShort } from "utils/dateFormats";
import DeliveryBasicDetailCompact from "./DeliveryBasicDetailCompact";
import DeliveryShipmentsCompact from "./DeliveryShipmentsCompact";

const PaymentRow = ({ label, value }) => (
  <Box sx={{ display: "flex", py: 0.25 }}>
    <Box sx={{ width: "45%", color: "text.primary", fontSize: "0.7rem", fontWeight: 600 }}>
      {label}
    </Box>
    <Box sx={{ width: "55%", fontSize: "0.74rem", fontWeight: 500, wordBreak: "break-word" }}>
      {value ?? "N/A"}
    </Box>
  </Box>
);

const DeliveryPaymentCompact = () => {
  const { deliveryDetail } = useDeliveryDetailContext();
  const p = deliveryDetail?.paymentDetails;
  if (!p) return null;

  return (
    <Box sx={{ p: 1 }}>
      <PaymentRow label="Transaction ID" value={p?.nQrTxnId} />
      <PaymentRow label="Amount" value={p?.amount != null ? `Rs. ${p.amount}` : "N/A"} />
      <PaymentRow label="Payer Name" value={p?.payerName} />
      <PaymentRow label="Payer Phone" value={p?.payerMobileNumber} />
      <PaymentRow label="Merchant" value={p?.merchantName} />
      <PaymentRow label="Bill Number" value={p?.merchantBillNo} />
      <PaymentRow
        label="Payment Date"
        value={p?.paymentDate ? localizeDateTimeShort(p.paymentDate) : "N/A"}
      />
    </Box>
  );
};

const DeliveryDetailClassicView = () => {
  const { deliveryDetail } = useDeliveryDetailContext();
  const deliveryId = deliveryDetail?._id;

  const hasPaymentDetails =
    !!deliveryDetail?.paymentDetails &&
    typeof deliveryDetail.paymentDetails === "object" &&
    Object.keys(deliveryDetail.paymentDetails).length > 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 2,
        borderRadius: 2,
        bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "grey.50"),
      }}
    >
      {/* Row 1: Delivery Details | Payment (when available) */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: hasPaymentDetails ? "7fr 5fr" : "1fr" },
          gap: 2,
          alignItems: "stretch",
        }}
      >
        <CompactCard title="Delivery Details" icon={Inventory2Outlined} maxHeight={480}>
          <DeliveryBasicDetailCompact />
        </CompactCard>
        {hasPaymentDetails && (
          <CompactCard title="Payment" icon={PaymentsOutlined} maxHeight={480}>
            <DeliveryPaymentCompact />
          </CompactCard>
        )}
      </Box>

      {/* Row 2: Notes | Documents */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, alignItems: "stretch" }}>
        <CompactCard title="Notes" icon={StickyNote2Outlined} maxHeight={340}>
          <ShipmentNotesCompact source="Delivery" sourceRef={deliveryId} isActive={deliveryDetail?.isActive} />
        </CompactCard>
        <CompactCard title="Documents" icon={DescriptionOutlined} maxHeight={340}>
          <ShipmentDocumentsCompact source="Delivery" sourceRef={deliveryId} isActive={deliveryDetail?.isActive} />
        </CompactCard>
      </Box>

      {/* Row 3: Shipments */}
      <CompactCard title="Shipments" icon={LocalShippingOutlined} maxHeight={420} noScroll>
        <DeliveryShipmentsCompact />
      </CompactCard>

      {!deliveryId && (
        <Typography sx={{ fontSize: "0.78rem", color: "text.secondary", textAlign: "center", py: 2 }}>
          Loading delivery...
        </Typography>
      )}
    </Box>
  );
};

export default DeliveryDetailClassicView;
